import { useCallback, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useUserProjectRole } from 'portal/lib/hooks'
import { eventsCountLoad, eventsLoad } from 'portal/store/events/actions'

export const useInfractionEventsLoader = (
  startDate,
  endDate,
  moduleName,
  showDismissed = 'false',
  tableFilters = {},
  pageSize = 100
) => {
  const dispatch = useDispatch()
  const { accountId, projectId } = useParams()

  const { isReadOnly } = useUserProjectRole(accountId, projectId)

  // Clean tableFilters by removing empty string values that would cause API issues
  const cleanedTableFilters = useMemo(() => {
    const cleanFilters = filters => {
      return Object.entries(filters).reduce((acc, [key, value]) => {
        // Handle arrays
        if (Array.isArray(value)) {
          // Filter out empty strings and whitespace-only strings
          const cleanedArray = value.filter(
            item => item !== '' && item !== ' ' && item?.trim?.() !== ''
          )
          // Only include array if it has valid items
          if (cleanedArray.length > 0) {
            acc[key] = cleanedArray
          }
          return acc
        }

        // Include all other non-empty values
        acc[key] = value
        return acc
      }, {})
    }

    return cleanFilters(tableFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableFilters)])

  const reload = useCallback(() => {
    const filterSourceTypes = ['Infraction']
    const filter = {
      startDate: startDate,
      endDate: endDate,
      sourceTypes: filterSourceTypes,
      submissionErrors: moduleName
        ? 'ExcludeSubmissionErrors'
        : 'SubmissionErrorsOnly',
      ...(showDismissed === 'false' ? { statuses: ['Open'] } : {}),
      moduleName: moduleName ? moduleName : undefined
    }

    // Combined Filters
    const infractionEventFilters = {
      ...filter,
      ...cleanedTableFilters
    }

    // If it exists, reformat the moduleName filter to be a single string
    if (infractionEventFilters.moduleName) {
      if (Array.isArray(infractionEventFilters.moduleName)) {
        // if only one moduleName use that one, if multiple, remove the filter
        if (infractionEventFilters.moduleName.length > 1) {
          delete infractionEventFilters.moduleName
        } else {
          infractionEventFilters.moduleName =
            infractionEventFilters.moduleName[0]
        }
      }
    }

    dispatch(
      eventsLoad(
        accountId,
        projectId,
        moduleName,
        pageSize,
        null,
        [],
        infractionEventFilters
      )
    )

    dispatch(
      eventsCountLoad(accountId, projectId, moduleName, infractionEventFilters)
    )
  }, [
    startDate,
    endDate,
    showDismissed,
    cleanedTableFilters,
    dispatch,
    accountId,
    projectId,
    moduleName,
    pageSize
  ])

  useEffect(() => {
    reload()
  }, [reload])

  const {
    loading,
    error,
    items,
    continuationToken,
    loadingCount,
    errorCount,
    count
  } = useSelector((state: any) => {
    return state.events[moduleName ? moduleName : 'submission']
  })

  function totalAlertsCount() {
    if (loadingCount) {
      return '....'
    }
    if (errorCount !== null) {
      return 'Error fetching total alerts'
    }
    return count
  }

  const handlePageChange = useCallback(() => {
    if (continuationToken === null) {
      return
    }
    const filter = {
      startDate: startDate,
      endDate: endDate,
      sourceTypes: ['Infraction'],
      submissionErrors: moduleName
        ? 'ExcludeSubmissionErrors'
        : 'SubmissionErrorsOnly',
      ...(showDismissed === 'false' ? { statuses: ['Open'] } : {})
    }

    // Table Filters
    const infractionEventFilters = {
      ...filter,
      ...cleanedTableFilters
    }

    dispatch(
      eventsLoad(
        accountId,
        projectId,
        moduleName,
        pageSize,
        continuationToken,
        items,
        infractionEventFilters
      )
    )
  }, [
    dispatch,
    accountId,
    projectId,
    moduleName,
    pageSize,
    continuationToken,
    items,
    startDate,
    endDate,
    showDismissed,
    cleanedTableFilters
  ])

  const hasNextPage = continuationToken != null

  return {
    loading,
    error,
    items,
    count,
    reload,
    handlePageChange,
    hasNextPage,
    totalAlertsCount,
    readOnly: isReadOnly
  }
}
